// Deleting a job — what goes with it, counted.
//
// The dialog frame (naming the record, the irreversibility, the danger button, the
// stay-open-on-failure behaviour) is DeleteConfirmModal, shared with the retainer
// delete. All that is here is what a job destroys and how it is worded.
import useAsync from '../../hooks/useAsync';
import { jobsApi } from '../../api';
import he from '../../i18n/he';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

// Money rides along only where it means something: what was invoiced, what was
// actually collected against it, and what the expenses came to.
const lines = (p) => [
  { key: 'invoices', count: p.invoices, label: he.common.deleteInvoices, amount: p.invoicedTotal },
  { key: 'payments', count: p.payments, label: he.common.deletePayments, amount: p.collectedTotal },
  { key: 'expenses', count: p.expenses, label: he.common.deleteExpenses, amount: p.expenseTotal },
  { key: 'tasks', count: p.tasks, label: he.common.deleteTasks },
  { key: 'meetings', count: p.meetings, label: he.calendar.deleteMeetings },
  { key: 'publications', count: p.publications, label: he.common.deletePublications },
  { key: 'files', count: p.files, label: he.common.deleteFiles },
  // Named explicitly rather than folded into the file count: a supplier's signed
  // contract may exist nowhere else, and its stored copy is destroyed with the job.
  { key: 'agreements', count: p.agreements, label: he.common.deleteAgreements },
  // The work content writers sent in (064). Its own line rather than folded into the
  // file count above, for the same reason the contracts have one: nobody here filed it,
  // and a dialog that says '12 files' warns about somebody else's deliverables not at
  // all.
  { key: 'workFiles', count: p.workFiles, label: he.common.deleteWorkFiles },
].filter((line) => line.count > 0);

export default function DeleteJobModal({ job, onClose, onDeleted }) {
  const { data: preview, loading, error } = useAsync(() => jobsApi.deletionPreview(job.id), [job.id]);

  return (
    <DeleteConfirmModal
      title={he.jobs.deleteJob}
      loading={loading}
      error={error}
      name={preview?.title}
      subtitle={preview?.client_name}
      // A topic's blast radius is the question everyone asks first, so it is answered
      // before it is asked.
      scopeNote={preview?.income_source === 'retainer' ? he.jobs.deleteTopicScope : null}
      items={preview ? lines(preview) : []}
      // The money consequence, said out loud: deleting a job with collected payments
      // on it also changes who owes whom, which no list of counts makes obvious.
      note={preview && (preview.payments > 0 || preview.expenses > 0) ? he.jobs.deleteBalanceNote : null}
      onConfirm={() => jobsApi.remove(job.id)}
      onClose={onClose}
      onDeleted={onDeleted}
    />
  );
}
